'use strict'
import * as React from 'react'
import { CircularProgress, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

import { ReactComponent as UndoIcon } from '../../assets/icon/undo.svg'

const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * スピナーボタン
 *
 * @param {Object} props プロパティ
 * @returns ボタン
 */
export const SpinnerButton = (props) => {
  const name = props.name
  const className = props.className
  const disabled = props.disabled
  const onClick = props.onClick
  const disableSpinner = props?.disableSpinner || false
  // 処理中フラグ
  const isProsessing = React.useRef(false)
  // スピナー表示
  const [isSpinner, setIsSpinner] = React.useState(false)

  const handleClick = async (e) => {
    if (isProsessing.current) {
      return
    }
    try {
      isProsessing.current = true
      setIsSpinner(true)
      await sleep(200)
      await onClick(e)
    } finally {
      await sleep(300)
      isProsessing.current = false
      setIsSpinner(false)
    }
  }

  return (
    <button
      data-testid={props?.['data-testid']}
      type="button"
      className={className}
      disabled={disabled || isSpinner}
      onClick={(e) => handleClick(e)}
    >
      {isSpinner && !disableSpinner ? (
        <div style={{ position: 'relative' }}>
          <span className="pr-3" style={{ position: 'absolute', left: '25px', top: '2px' }}>
            <CircularProgress size={18} style={{ color: 'white' }} />
          </span>
          <span>処理中</span>
        </div>
      ) : (
        <span>{name}</span>
      )}
    </button>
  )
}

export const RefleshButton = (props) => {
  const name = props.name
  const className = props.className
  const disabled = props.disabled
  const onClick = props.onClick
  // 処理中フラグ
  const isProsessing = React.useRef(false)
  // アニメーション表示
  const [isAnimation, setIsAnimation] = React.useState(false)

  const handleClick = async (e) => {
    if (isProsessing.current) {
      return
    }
    try {
      isProsessing.current = true
      setIsAnimation(true)
      await sleep(200)
      await onClick(e)
    } finally {
      await sleep(300)
      isProsessing.current = false
      setIsAnimation(false)
    }
  }

  return (
    <button
      data-testid={props?.['data-testid']}
      type="button"
      className={className}
      disabled={disabled || isAnimation}
      onClick={(e) => handleClick(e)}
    >
      {name}
      <RefreshIcon className={isAnimation ? 'reflesh-animation' : ''} />
    </button>
  )
}

/**
 * 削除ボタン 削除及び、リストアを行う際に利用するボタン
 *
 * @typedef {Object} DeleteButtonProps
 * @property {string} className
 * @property {boolean} disabled
 * @property {function} onDelete
 * @property {function} onRestore
 * @property {boolean} isRestore
 */

/** @type {React.FC<DeleteButtonProps>} */
export const DeleteButton = ({ className = '', disabled = false, onDelete, onRestore, isRestore = false }) => {
  const beforeClasses = [
    'before:absolute',
    'before:block',
    "before:content-['']",
    'before:h-[30px]',
    'before:w-[30px]',
    'before:z-0',
    'before:rounded-full',
    'before:hidden',
    'before:bg-gray-600/10'
  ]
  const before = beforeClasses.join(' ')

  if (isRestore) {
    return (
      <IconButton
        title="復元"
        size="small"
        disabled={disabled}
        className={`!p-0 relative !text-gray-600/50 ${before} hover:before:block  `}
        onClick={onRestore}
      >
        <UndoIcon className={'w-[20px] h-[20px]'} />
      </IconButton>
    )
  }

  return (
    <IconButton
      title="削除"
      disabled={disabled}
      size="small"
      className={`!p-0 relative !text-gray-600/50 ${before} hover:before:block ${className} `}
      onClick={onDelete}
      data-testid="delete"
    >
      <DeleteIcon className="relative z-[1]" fontSize="small" />
    </IconButton>
  )
}

/**
 * コピーボタン データコピーを行う際に利用するボタン
 *
 * @typedef {Object} CopyButtonProps
 * @property {string} [label] - ラベル（デフォルト表示）
 * @property {boolstringean} [completedLabel] - ラベル（コピー完了後）
 * @property {string} [className] - クラス名
 * @property {boolean} [disabled] - 非活性フラグ
 * @property {function} onClick - クリック時のハンドラー
 */

/** @type {React.FC<CopyButtonProps>} */
export const CopyButton = (props) => {
  // 定数
  const DEFAULT_LABEL = '前期設定をコピー'
  const COMPLETED_LABEL = 'コピーしました'
  const RESET_TIMEOUT_MS = 4000

  const { label = DEFAULT_LABEL, completedLabel = COMPLETED_LABEL, className = '', disabled = false, onClick } = props

  const [status, setStatus] = React.useState('idle') // 'idle' | 'loading' | 'completed'
  const timeoutRef = React.useRef(null)

  const defaultClasses = [
    'min-w-[180px]',
    'text-white',
    'bg-[#383947]',
    'hover:bg-[#383947b3]',
    'disabled:text-[#F2F8FF]',
    'disabled:bg-[#383947b3]',
    'px-[15px]',
    'py-[10px]',
    'text-h7',
    'text-center',
    'rounded-[8px]',
    'flex',
    'items-center',
    'space-x-2'
  ]
  const defaultClass = defaultClasses.join(' ')

  const resetStatus = React.useCallback(() => {
    setStatus('idle')
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const handleClick = React.useCallback(async () => {
    if (disabled || status !== 'idle') return

    setStatus('loading')

    try {
      if (onClick) {
        const result = onClick()
        if (result && typeof result.then === 'function') {
          await result
        }
      }
      setStatus('completed')
    } catch (error) {
      console.error('コピー時にエラー発生：', error)
      setStatus('idle')
    }

    timeoutRef.current = setTimeout(() => {
      resetStatus()
    }, RESET_TIMEOUT_MS)
  }, [disabled, status, onClick, resetStatus])

  const handleReset = React.useCallback(() => {
    // loading状態のときはリセットしない
    if (status !== 'loading') {
      resetStatus()
    }
  }, [status, resetStatus])

  // コンポーネントのアンマウント時にタイマーをクリア
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])
  const currentLabel = status === 'completed' ? completedLabel : label
  const isDisabled = disabled || status === 'loading' || status === 'completed'

  const renderIcon = () => {
    if (status === 'loading') {
      return <CircularProgress size={16} color="inherit" />
    }
    return status === 'completed' ? <CheckIcon /> : <ContentCopyIcon />
  }

  return (
    <button
      data-testid={props?.['data-testid']}
      type="button"
      className={`${defaultClass} ${className}`}
      disabled={isDisabled}
      onClick={handleClick}
      onBlur={!isDisabled ? handleReset : undefined}
      onMouseLeave={handleReset}
      aria-disabled={isDisabled}
    >
      {renderIcon()}
      <span>{currentLabel}</span>
    </button>
  )
}

export const RegisterButton = (props) => {
  const { title, onClick, disabled, className } = props

  return (
    <div className="btn-group inline-flex  ">
      <button
        data-testid={props?.['data-testid']}
        disabled={disabled}
        className={`header-btn white--text ${className}`}
        color="inherit"
        onClick={onClick}
      >
        <span className="  inline-flex items-center">
          <span className="icon-plus-line icon-32px  my-px"></span>
          <span className="-translate-y-px text-h7">{title}</span>
        </span>
      </button>
    </div>
  )
}
